import { LineSeries as LineSeriesDefinition } from "lightweight-charts";
import merge from "lodash/merge";
import { ChartCandle, LineData, LineSeriesOptions, Series } from "../chart.typings";
import BaseChartSeries from "./baseSeries";
import { lineSeriesOptions } from "../../../assets/constants";
import { getLineTypeFromChartSubType } from "../../../util/chart";
import { ChartSubType } from "../../../proto/generated/chart";
import { DeepPartial, MarkerType } from "lightweight-charts";
import { IndicatorOutput_OutputStyle } from "../../../proto/generated/indicator";
import { evaluateExpression } from "../../../util/safeExpressionEval";
import { ChartLineStyle } from "../../../pages/chartPage/ChartPage";

class LineSeries extends BaseChartSeries<LineData, LineSeriesOptions> {

    addSeriesToChart() {
        return this.createSeries(LineSeriesDefinition, this.seriesOptions)
    }

    getDefaultOptions(): LineSeriesOptions {
        const { thickness, style } = this.config;
        const lineType = getLineTypeFromChartSubType(this.config.chartSubType || ChartSubType.SIMPLE);

        const options: LineSeriesOptions = {
            lineType,
            source: "close",
            lineWidth: thickness || 2,
            markerType: style === IndicatorOutput_OutputStyle.WITH_DIAMONDS ? MarkerType.Diamond : MarkerType.None
        };
        return merge({...lineSeriesOptions}, options, this.options)
    }
    onOptionsUpdate(options: DeepPartial<LineSeriesOptions>): void {
        if ('source' in options) {
            this._setData(this.rawData, this.metadata)
        }
    }
    onConfigUpdate(oldConfig: Series): void {
        if (oldConfig.chartSubType !== this.config.chartSubType) {
            this.updateOptions({})
        }
    }

    private resolveSourceValue(candle: ChartCandle, source: string): number | undefined {
        switch (source) {
            case 'hl2':
                return (candle.high + candle.low) / 2
            case 'hlc3':
                return (candle.high + candle.low + candle.close) / 3
            case 'ohlc4':
                return (candle.open + candle.high + candle.low + candle.close) / 4
            default:
                return candle[source] as number | undefined
        }
    }

    generateSeriesData(data: ChartCandle[], metadata: Record<string, any> = {}): LineData[] {
        const result: LineData[] = [];
        const open: number[] = new Array(data.length);
        const close: number[] = new Array(data.length);
        const series: number[] = new Array(data.length);

        const {color: seriesColor} = this.seriesApi.options()

        const {color: metaColor, lineStyle } = metadata;
        const {color: configColor} = this.config;
        const isColorHexCode = metaColor && RegExp(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/).test(metaColor)
        const color = isColorHexCode ? configColor : metaColor;
        const source = this.seriesOptions.source

        if (isColorHexCode && seriesColor !== color) {
            this.seriesApi.applyOptions({color})
        }

        // Only persist when changed: updateOptions pushes a chart config update (Redux + React re-render),
        // and this runs on every live tick.
        const nextLineStyle = lineStyle ? ChartLineStyle[lineStyle] as any : undefined
        if (nextLineStyle !== undefined && this.seriesApi.options().lineStyle !== nextLineStyle) {
            this.updateOptions({
                lineStyle: nextLineStyle
            })
        }


        data.forEach((candle, i) => {
            const sourceValue = this.resolveSourceValue(candle, source)
            if (sourceValue === undefined) return;
            open[i] = candle.open;
            close[i] = candle.close;
            series[i] = candle.series || 0
            let candleColor = color;
            if (isNaN(sourceValue) && this.config.style === IndicatorOutput_OutputStyle.WITH_BREAKS) {
                candleColor = "transparent"
            } else if (!isColorHexCode && candleColor) {
                candleColor = evaluateExpression<string>(metaColor, { open, close, series, candle, i })
            }

            result.push({
                ...candle,
                value: sourceValue,
                color: candleColor && (candleColor == "transparent" || !isColorHexCode) ? candleColor : undefined
            })

        })

        return result;
    }

}

export default LineSeries;